import { useState } from "react"
import { ClipboardCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  smeFeedbackAllowed,
  submitSmeFeedback,
  type SmeVerdict,
} from "@/lib/smeFeedback"
import type { SmeFeedback } from "@/types/chatHistory"

const VERDICTS: { value: SmeVerdict; label: string }[] = [
  { value: "correct", label: "Correct" },
  { value: "partly", label: "Partly" },
  { value: "incorrect", label: "Incorrect" },
]

interface SmeFeedbackControlProps {
  chatId: string
  assistantTimestamp: string
  /** Persisted verdict from a previous session (rehydrated from chat history). */
  existing?: SmeFeedback
}

/**
 * #53 — dev-only SME review control, shown under every finished assistant answer.
 * The verdict + comment become `sme-feedback` scores on the answer's (and dive's)
 * Langfuse traces, so reviewed runs are filterable there with full call metadata.
 * Never rendered outside dev builds (smeFeedbackAllowed), and the backend endpoint
 * independently 404s outside dev — this feature cannot reach prod.
 */
export function SmeFeedbackControl({
  chatId,
  assistantTimestamp,
  existing,
}: SmeFeedbackControlProps) {
  const [open, setOpen] = useState(false)
  const [verdict, setVerdict] = useState<SmeVerdict | null>(
    (existing?.verdict as SmeVerdict) ?? null
  )
  const [comment, setComment] = useState(existing?.comment ?? "")
  const [submitted, setSubmitted] = useState<SmeFeedback | undefined>(existing)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!smeFeedbackAllowed) return null

  const handleSubmit = async () => {
    if (!verdict) return
    setSubmitting(true)
    setError(null)
    try {
      await submitSmeFeedback(chatId, assistantTimestamp, verdict, comment.trim())
      setSubmitted({ verdict, comment: comment.trim() })
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Feedback submit failed")
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <div className="flex items-center gap-2 px-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          onClick={() => setOpen(true)}
        >
          <ClipboardCheck className="h-3.5 w-3.5" />
          {submitted ? `SME review: ${submitted.verdict}` : "SME review"}
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-1 mt-1 space-y-2 rounded-lg border border-border bg-background p-3">
      <p className="text-xs font-medium text-muted-foreground">
        SME review — is this answer (and its deep dive, if any) correct?
      </p>
      <div className="flex items-center gap-1.5">
        {VERDICTS.map((v) => (
          <Button
            key={v.value}
            type="button"
            size="sm"
            variant={verdict === v.value ? "secondary" : "outline"}
            className="h-7 px-2.5 text-xs"
            aria-pressed={verdict === v.value}
            onClick={() => setVerdict(v.value)}
          >
            {v.label}
          </Button>
        ))}
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What is wrong or missing? Cite the provision if you can."
        className="min-h-16 text-sm"
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex items-center justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2.5 text-xs"
          disabled={submitting}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 px-2.5 text-xs"
          disabled={!verdict || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Saving…" : "Submit review"}
        </Button>
      </div>
    </div>
  )
}
